/**
 * Job and Processing Enums
 *
 * Enums for background jobs, exports, and processing tasks.
 * Issue #313 - Centralize enums to shared-types
 */

import { z } from 'zod';

// ============================================================================
// BACKGROUND JOB STATUS
// ============================================================================

/**
 * Background job status
 * Used by: Enrichment jobs, export jobs, import jobs
 */
export enum BackgroundJobStatus {
  /** Job is waiting to be processed */
  PENDING = 'pending',
  /** Job is currently being processed */
  RUNNING = 'running',
  /** Job completed successfully */
  COMPLETED = 'completed',
  /** Job failed */
  FAILED = 'failed',
  /** Job was cancelled */
  CANCELLED = 'cancelled',
}

/** Zod schema for BackgroundJobStatus validation */
export const BackgroundJobStatusSchema = z.nativeEnum(BackgroundJobStatus);

/** Array of all job statuses for iteration */
export const BACKGROUND_JOB_STATUSES = Object.values(BackgroundJobStatus);

// ============================================================================
// ENRICHMENT JOB STATUS
// ============================================================================

/**
 * Enrichment job status with additional states
 */
export enum EnrichmentJobStatus {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  PARTIAL = 'PARTIAL',
}

/** Zod schema for EnrichmentJobStatus validation */
export const EnrichmentJobStatusSchema = z.nativeEnum(EnrichmentJobStatus);

// ============================================================================
// IMPORT JOB STATUS
// ============================================================================

/**
 * Import job status with searching state
 */
export enum ImportJobStatus {
  PENDING = 'pending',
  SEARCHING = 'searching',
  IMPORTING = 'importing',
  COMPLETED = 'completed',
  FAILED = 'failed',
  CANCELLED = 'cancelled',
}

/** Zod schema for ImportJobStatus validation */
export const ImportJobStatusSchema = z.nativeEnum(ImportJobStatus);

// ============================================================================
// IMPORT MODE
// ============================================================================

/**
 * Data import modes
 */
export enum ImportMode {
  /** Create new records only */
  CREATE = 'CREATE',
  /** Update existing records only */
  UPDATE = 'UPDATE',
  /** Create or update records */
  UPSERT = 'UPSERT',
}

/** Zod schema for ImportMode validation */
export const ImportModeSchema = z.nativeEnum(ImportMode);

// ============================================================================
// EXPORT FORMAT
// ============================================================================

/**
 * Data export formats
 */
export enum ExportFormat {
  CSV = 'CSV',
  XLSX = 'XLSX',
  JSON = 'JSON',
}

/** Zod schema for ExportFormat validation */
export const ExportFormatSchema = z.nativeEnum(ExportFormat);

/** Array of all export formats for iteration */
export const EXPORT_FORMATS = Object.values(ExportFormat);

// ============================================================================
// DELIVERY STATUS
// ============================================================================

/**
 * Generic delivery status for notifications, messages, etc.
 */
export enum DeliveryStatus {
  PENDING = 'pending',
  SENT = 'sent',
  DELIVERED = 'delivered',
  FAILED = 'failed',
  BOUNCED = 'bounced',
}

/** Zod schema for DeliveryStatus validation */
export const DeliveryStatusSchema = z.nativeEnum(DeliveryStatus);

/** Array of all delivery statuses for iteration */
export const DELIVERY_STATUSES = Object.values(DeliveryStatus);
